import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  CatalogService,
  MarketplaceSeller,
  MarketplaceSellerOfferSummary,
  OfferingAttributeAssignment,
  StorefrontPublication
} from './catalog.service';
import { forkJoin } from 'rxjs';
import {
  STOREFRONT_PUBLICATION_BADGE_FIELDS,
  STOREFRONT_PUBLICATION_SEO_FIELDS,
  createEmptyPublicationBadgePayload,
  normalizePublicationSeoJson,
  parsePublicationBadgesJson,
  serializePublicationBadgesJson
} from './storefront-publishing-schemas';

@Component({
  standalone: true,
  selector: 'ain-offering-storefront-publishing-panel',
  imports: [
    AinStateComponent,
    InfoHintComponent,
    MarketplaceStoreSelectorComponent,
    JalaliDateInputComponent,
    StructuredJsonFormComponent,
    AinAmountPipe,
    AinDatePipe,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    CheckboxModule,
    RouterLink
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.offerings.storefront.hint.title'"
      [descriptionKey]="'catalog.offerings.storefront.hint.description'" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      <section class="filters">
        <label>
          <span>{{ t('catalog.marketplace.filters.store') }}</span>
          <ain-marketplace-store-selector [(ngModel)]="selectedStoreId" (ngModelChange)="loadPublications()" />
        </label>
        <div class="actions">
          <button
            pButton
            type="button"
            [label]="t('catalog.storefront.publishing.create')"
            [disabled]="!selectedStoreId"
            (click)="openCreate()"></button>
          <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" [disabled]="!selectedStoreId" (click)="loadPublications()"></button>
          <a pButton routerLink="/marketplace/storefront/publishing" severity="secondary" [label]="t('catalog.offerings.storefront.bulkLink')"></a>
        </div>
      </section>

      @if (!selectedStoreId) {
        <ain-state type="empty" [title]="t('catalog.storefront.selectStore')" />
      } @else {
        <p-table [value]="publications()" [paginator]="publications().length > 10" [rows]="10">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('catalog.storefront.publishing.order') }}</th>
              <th>{{ t('catalog.storefront.publishing.visibleFrom') }} / {{ t('catalog.storefront.publishing.visibleTo') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ t(statusKey(row.status)) }}</td>
              <td>{{ row.displayOrder }}</td>
              <td>
                {{ row.visibleFromUtc ? (row.visibleFromUtc | ainDate) : '—' }} /
                {{ row.visibleToUtc ? (row.visibleToUtc | ainDate) : '—' }}
              </td>
              <td class="row-actions">
                <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.action.edit')" (click)="openEdit(row)"></button>
                @if (row.status !== 'Published') {
                  <button pButton type="button" size="small" [label]="t('catalog.action.publish')" (click)="publish(row)"></button>
                } @else {
                  <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.action.unpublish')" (click)="unpublish(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }

      @if (canViewMarketplace()) {
        <section class="marketplace-block">
          <h4>{{ t('catalog.offerings.storefront.marketplaceOffers') }}</h4>
          <p-table [value]="marketplaceOffers()" [paginator]="marketplaceOffers().length > 10" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.marketplace.sellers.name') }}</th>
                <th>{{ t('columns.currency') }}</th>
                <th>{{ t('columns.basePrice') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                <td>{{ sellerLabel(row.sellerId) }}</td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ row.price | ainAmount: 0 }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    }

    <p-dialog [header]="dialogTitle()" [(visible)]="dialogVisible" [modal]="true" [style]="{ width: '640px' }">
      <div class="form">
        <section class="form-section attribute-summary">
          <div class="section-head">
            <h4>{{ t('catalog.storefront.publishing.attributes.section') }}</h4>
            <a [routerLink]="['/catalog/offerings', offeringId]" [queryParams]="{ tab: 'attributes' }" class="manage-link">
              {{ t('catalog.storefront.publishing.attributes.manageLink') }}
            </a>
          </div>
          <ain-info-hint
            [titleKey]="'catalog.storefront.publishing.attributes.hint.title'"
            [descriptionKey]="'catalog.storefront.publishing.attributes.hint.description'" />
          @if (attributesLoading()) {
            <p class="section-hint">{{ t('common.loading') }}</p>
          } @else if (offeringAttributes().length === 0) {
            <p class="section-hint">{{ t('catalog.storefront.publishing.attributes.empty') }}</p>
          } @else {
            @if (capabilityAttributes().length > 0) {
              <div class="summary-block">
                <strong>{{ t('catalog.storefront.publishing.attributes.capabilities') }}</strong>
                <div class="tag-row">
                  @for (item of capabilityAttributes(); track item.id) {
                    <span class="summary-tag" [class.enabled]="isTruthyAttribute(item)">{{ item.displayName || item.key }}</span>
                  }
                </div>
              </div>
            }
            @if (metadataAttributes().length > 0) {
              <div class="summary-block">
                <strong>{{ t('catalog.storefront.publishing.attributes.metadata') }}</strong>
                <ul class="summary-list">
                  @for (item of metadataAttributes(); track item.id) {
                    <li><span>{{ item.displayName || item.key }}</span><em>{{ formatAttributeSummaryValue(item) }}</em></li>
                  }
                </ul>
              </div>
            }
          }
        </section>

        @if (isEditMode()) {
          <p class="status-chip">{{ t('columns.status') }}: {{ t(statusKey(editingStatus)) }}</p>
        }

        <label>
          <span>{{ t('catalog.storefront.publishing.order') }}</span>
          <input pInputText type="number" [(ngModel)]="displayOrder" class="full" />
        </label>
        <label>
          <span>{{ t('catalog.storefront.publishing.visibleFrom') }}</span>
          <ain-date-input [(ngModel)]="visibleFromUtc" class="full" />
        </label>
        <label>
          <span>{{ t('catalog.storefront.publishing.visibleTo') }}</span>
          <ain-date-input [(ngModel)]="visibleToUtc" class="full" />
        </label>
        <label class="checkbox-row">
          <p-checkbox [(ngModel)]="isPlatformDirectSaleAllowed" [binary]="true" />
          <span>{{ t('catalog.storefront.publishing.platformDirectSale') }}</span>
        </label>
        <p class="section-hint">{{ t('catalog.storefront.publishing.platformDirectSaleHint') }}</p>

        <section class="form-section">
          <div class="section-head">
            <h4>{{ t('catalog.storefront.publishing.badges.section') }}</h4>
            <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.publishing.badges.add')" (click)="addBadge()"></button>
          </div>
          @if (!badgePayloads.length) {
            <p class="section-hint">{{ t('catalog.storefront.publishing.badges') }}</p>
          } @else {
            @for (payload of badgePayloads; track $index) {
              <article class="badge-card">
                <div class="badge-card-head">
                  <strong>{{ t('catalog.storefront.publishing.badges') }} {{ $index + 1 }}</strong>
                  <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('catalog.storefront.publishing.badges.remove')" (click)="removeBadge($index)"></button>
                </div>
                <ain-structured-json-form [fields]="badgeFields" [(payloadJson)]="badgePayloads[$index]" />
              </article>
            }
          }
        </section>

        <section class="form-section">
          <h4>{{ t('catalog.storefront.publishing.seo.section') }}</h4>
          <ain-structured-json-form [fields]="seoFields" [(payloadJson)]="seoOverrideJson" />
        </section>

        @if (dialogError()) {
          <p class="error">{{ dialogError() }}</p>
        }
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDialog()"></button>
        <button pButton type="button" [label]="dialogSaveLabel()" [disabled]="saving()" (click)="savePublication()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .filters { display: grid; gap: 10px; margin: 12px 0; }
    .filters label { display: grid; gap: 6px; max-width: 360px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .row-actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .marketplace-block { margin-top: 20px; }
    .marketplace-block h4 { margin: 0 0 8px; font-size: 14px; }
    .error { color: #b91c1c; margin-top: 8px; }
    .form { display: grid; gap: 10px; }
    .form label > span { color: #475569; font-size: 12px; font-weight: 600; }
    .form-section { display: grid; gap: 10px; padding-top: 4px; border-top: 1px solid #e2e8f0; }
    .form-section h4 { margin: 0; font-size: 14px; }
    .section-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .section-hint { margin: 0; color: #64748b; font-size: 13px; }
    .badge-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 8px; background: #f8fafc; }
    .badge-card-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .status-chip { margin: 0; color: #475569; font-size: 13px; font-weight: 600; }
    .attribute-summary .manage-link { color: #1d4ed8; font-weight: 600; text-decoration: none; font-size: 13px; }
    .summary-block { display: grid; gap: 8px; }
    .summary-block strong { font-size: 13px; color: #334155; }
    .tag-row { display: flex; flex-wrap: wrap; gap: 6px; }
    .summary-tag { border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 10px; font-size: 12px; color: #64748b; background: #f8fafc; }
    .summary-tag.enabled { border-color: #86efac; color: #166534; background: #ecfdf5; }
    .summary-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
    .summary-list li { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; }
    .summary-list span { color: #475569; }
    .summary-list em { font-style: normal; color: #0f172a; font-weight: 600; text-align: right; }
    .full { width: 100%; }
  `]
})
export class OfferingStorefrontPublishingPanelComponent implements OnInit {
  @Input({ required: true }) offeringId!: string;

  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly currentContext = inject(CurrentContextService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly dialogError = signal('');
  readonly publications = signal<StorefrontPublication[]>([]);
  readonly marketplaceOffers = signal<MarketplaceSellerOfferSummary[]>([]);
  private readonly sellersById = signal<Map<string, MarketplaceSeller>>(new Map());
  readonly offeringAttributes = signal<OfferingAttributeAssignment[]>([]);
  readonly attributesLoading = signal(false);
  readonly editingPublicationId = signal<string | null>(null);

  readonly badgeFields = STOREFRONT_PUBLICATION_BADGE_FIELDS;
  readonly seoFields = STOREFRONT_PUBLICATION_SEO_FIELDS;

  readonly capabilityAttributes = () =>
    this.offeringAttributes().filter(item => item.key.startsWith('capability_'));

  readonly metadataAttributes = () =>
    this.offeringAttributes().filter(item => !item.key.startsWith('capability_'));

  selectedStoreId: string | null = null;
  dialogVisible = false;
  displayOrder = 10;
  visibleFromUtc: string | null = null;
  visibleToUtc: string | null = null;
  isPlatformDirectSaleAllowed = false;
  seoOverrideJson = '{}';
  badgePayloads: string[] = [];
  editingStatus = '';
  editingCategoryId: string | null = null;
  editingMerchandisingTagsJson = '[]';

  ngOnInit(): void {
    this.loading.set(false);
    this.loadOfferingAttributeSummary();
    if (this.canViewMarketplace()) {
      this.loadMarketplaceOffers();
    }
  }

  sellerLabel(sellerId: string): string {
    const seller = this.sellersById().get(sellerId);
    return seller ? `${seller.sellerCode} - ${seller.displayName}` : sellerId;
  }

  loadMarketplaceOffers(): void {
    forkJoin({
      sellers: this.service.listMarketplaceSellers(),
      offers: this.service.listMarketplaceOffers(this.offeringId)
    }).subscribe({
      next: ({ sellers, offers }) => {
        this.sellersById.set(new Map(sellers.map(item => [item.id, item])));
        this.marketplaceOffers.set(offers);
      },
      error: () => {
        this.sellersById.set(new Map());
        this.marketplaceOffers.set([]);
      }
    });
  }

  canViewMarketplace(): boolean {
    const context = this.currentContext.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }
    const permissions = new Set((context?.permissions ?? []).map(item => item.key));
    return permissions.has('marketplace.seller.view');
  }

  isEditMode(): boolean {
    return Boolean(this.editingPublicationId());
  }

  dialogTitle(): string {
    return this.isEditMode()
      ? this.t('catalog.storefront.publishing.editTitle')
      : this.t('catalog.storefront.publishing.create');
  }

  dialogSaveLabel(): string {
    return this.isEditMode() ? this.t('catalog.action.save') : this.t('catalog.action.create');
  }

  statusKey(status: string): string {
    const map: Record<string, string> = {
      Draft: 'catalog.marketplace.storefront.status.draft',
      ReadyForReview: 'catalog.marketplace.storefront.status.draft',
      Published: 'catalog.marketplace.storefront.status.published',
      Hidden: 'catalog.marketplace.storefront.status.unpublished',
      Archived: 'catalog.marketplace.storefront.status.archived',
      Unpublished: 'catalog.marketplace.storefront.status.unpublished'
    };
    return map[status] ?? status;
  }

  loadPublications(): void {
    if (!this.selectedStoreId) {
      this.publications.set([]);
      return;
    }
    this.loading.set(true);
    this.service.listStorefrontPublications(this.selectedStoreId).subscribe({
      next: rows => {
        this.publications.set(rows.filter(item => item.offeringId === this.offeringId));
        this.loading.set(false);
      },
      error: () => {
        this.publications.set([]);
        this.error.set(this.t('catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    this.editingPublicationId.set(null);
    this.editingStatus = '';
    this.editingCategoryId = null;
    this.editingMerchandisingTagsJson = '[]';
    this.displayOrder = (this.publications().length + 1) * 10;
    this.visibleFromUtc = null;
    this.visibleToUtc = null;
    this.isPlatformDirectSaleAllowed = false;
    this.badgePayloads = [];
    this.seoOverrideJson = '{}';
    this.dialogError.set('');
    this.loadOfferingAttributeSummary();
    this.dialogVisible = true;
  }

  openEdit(row: StorefrontPublication): void {
    this.editingPublicationId.set(row.id);
    this.editingStatus = row.status;
    this.editingCategoryId = row.categoryId;
    this.editingMerchandisingTagsJson = row.merchandisingTagsJson?.trim() || '[]';
    this.displayOrder = row.displayOrder;
    this.visibleFromUtc = row.visibleFromUtc;
    this.visibleToUtc = row.visibleToUtc;
    this.badgePayloads = parsePublicationBadgesJson(row.badgesJson);
    this.seoOverrideJson = normalizePublicationSeoJson(row.seoOverrideJson);
    this.isPlatformDirectSaleAllowed = row.isPlatformDirectSaleAllowed;
    this.dialogError.set('');
    this.loadOfferingAttributeSummary();
    this.dialogVisible = true;
  }

  closeDialog(): void {
    this.dialogVisible = false;
    this.editingPublicationId.set(null);
    this.editingStatus = '';
    this.dialogError.set('');
  }

  addBadge(): void {
    this.badgePayloads = [...this.badgePayloads, createEmptyPublicationBadgePayload()];
  }

  removeBadge(index: number): void {
    this.badgePayloads = this.badgePayloads.filter((_, itemIndex) => itemIndex !== index);
  }

  loadOfferingAttributeSummary(): void {
    this.attributesLoading.set(true);
    this.service.getOfferingAttributes(this.offeringId).subscribe({
      next: items => {
        this.offeringAttributes.set(items);
        this.attributesLoading.set(false);
      },
      error: () => {
        this.offeringAttributes.set([]);
        this.attributesLoading.set(false);
      }
    });
  }

  isTruthyAttribute(item: OfferingAttributeAssignment): boolean {
    const value = item.value?.trim().toLowerCase() ?? '';
    return value === 'true' || value === '1';
  }

  formatAttributeSummaryValue(item: OfferingAttributeAssignment): string {
    if (item.type.toLowerCase() === 'boolean') {
      return this.isTruthyAttribute(item)
        ? this.t('catalog.offerings.attributes.value.true')
        : this.t('catalog.offerings.attributes.value.false');
    }
    return item.value?.trim() || '—';
  }

  savePublication(): void {
    if (!this.selectedStoreId) {
      return;
    }
    this.dialogError.set('');
    this.saving.set(true);
    this.service.upsertStorefrontPublication({
      storeId: this.selectedStoreId,
      offeringId: this.offeringId,
      categoryId: this.editingCategoryId,
      visibleFromUtc: this.visibleFromUtc || null,
      visibleToUtc: this.visibleToUtc || null,
      displayOrder: Number(this.displayOrder) || 0,
      badgesJson: serializePublicationBadgesJson(this.badgePayloads),
      seoOverrideJson: this.seoOverrideJson.trim() || '{}',
      merchandisingTagsJson: this.editingMerchandisingTagsJson,
      isPlatformDirectSaleAllowed: this.isPlatformDirectSaleAllowed
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.closeDialog();
        this.loadPublications();
      },
      error: () => {
        this.saving.set(false);
        this.dialogError.set(this.t('catalog.storefront.publishing.validation.saveFailed'));
      }
    });
  }

  publish(row: StorefrontPublication): void {
    this.service.publishStorefrontPublication(row.id).subscribe({
      next: () => this.loadPublications(),
      error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
    });
  }

  unpublish(row: StorefrontPublication): void {
    this.service.unpublishStorefrontPublication(row.id).subscribe({
      next: () => this.loadPublications(),
      error: () => this.error.set(this.t('catalog.offerings.updateFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
